import { createContext, useCallback, useContext } from 'react';
import type { ReactNode } from 'react';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import type {
  InformationPackageArtifactApi,
  InformationPackageArtifactContentApi,
  InformationPackageCreateApi,
  InformationPackageDetailApi,
  InformationPackageFeatureFilters,
  InformationPackageFeatureListApi,
  InformationPackageListApi,
  InformationPackageReindexApi,
  InformationPackageUnitDetailApi,
} from './InformationPackageApiProviderTypes';
import { buildUrl } from '../utils/buildQuery';

interface InformationPackageApiContextType {
  getInformationPackages: (
    skip?: number,
    limit?: number,
    searchName?: string,
    featureQuery?: string,
  ) => Promise<InformationPackageListApi>;
  deleteInformationPackage: (packageId: string) => Promise<void>;
  createInformationPackage: (data: FormData) => Promise<InformationPackageCreateApi>;
  getInformationPackageById: (packageId: string) => Promise<InformationPackageDetailApi>;
  reindexInformationPackage: (packageId: string) => Promise<InformationPackageReindexApi>;
  getInformationPackageUnitById: (unitId: string) => Promise<InformationPackageUnitDetailApi>;
  getInformationPackageArtifactById: (artifactId: string) => Promise<InformationPackageArtifactApi>;
  getInformationPackageUnitFeatures: (
    unitId: string,
    skip?: number,
    limit?: number,
    filters?: InformationPackageFeatureFilters,
  ) => Promise<InformationPackageFeatureListApi>;
  getArtifactContentById: (artifactId: string) => Promise<InformationPackageArtifactContentApi>;
}

const InformationPackageApiContext = createContext<InformationPackageApiContextType | null>(null);
const baseUrl = '/v1/information_package';

export function InformationPackageApiProvider({ children }: { children: ReactNode }) {
  const { get, post, del } = useApiService(CONFIG.SERVER_DOCUMENT);

  const getInformationPackages = useCallback(
    async (
      skip: number = 0,
      limit: number = 10,
      searchName?: string,
      featureQuery?: string,
    ): Promise<InformationPackageListApi> => {
      try {
        const response = await get(
          buildUrl(baseUrl, {
            skip,
            limit,
            name: searchName,
            feature_query: featureQuery,
          }),
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching information packages:', error);
        throw error;
      }
    },
    [get],
  );

  const deleteInformationPackage = useCallback(
    async (packageId: string): Promise<void> => {
      try {
        await del(`${baseUrl}/${packageId}`);
      } catch (error) {
        console.error('Error deleting information package:', error);
        throw error;
      }
    },
    [del],
  );

  const createInformationPackage = useCallback(
    async (data: FormData): Promise<InformationPackageCreateApi> => {
      try {
        const response = await post(baseUrl, data);
        return response.data;
      } catch (error) {
        console.error('Error creating information package:', error);
        throw error;
      }
    },
    [post],
  );

  const getInformationPackageById = useCallback(
    async (packageId: string): Promise<InformationPackageDetailApi> => {
      try {
        const response = await get(`${baseUrl}/${packageId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching information package by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const reindexInformationPackage = useCallback(
    async (packageId: string): Promise<InformationPackageReindexApi> => {
      try {
        const response = await post(`${baseUrl}/${packageId}/reindex`);
        return response.data;
      } catch (error) {
        console.error('Error reindexing information package:', error);
        throw error;
      }
    },
    [post],
  );

  const getInformationPackageUnitById = useCallback(
    async (unitId: string): Promise<InformationPackageUnitDetailApi> => {
      try {
        const response = await get(`${baseUrl}/units/${unitId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching information package unit by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getInformationPackageArtifactById = useCallback(
    async (artifactId: string): Promise<InformationPackageArtifactApi> => {
      try {
        const response = await get(`${baseUrl}/artifact/${artifactId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching information package artifact by ID:', error);
        throw error;
      }
    },
    [get],
  );

  const getInformationPackageUnitFeatures = useCallback(
    async (
      unitId: string,
      skip: number = 0,
      limit: number = 10,
      filters: InformationPackageFeatureFilters = {},
    ): Promise<InformationPackageFeatureListApi> => {
      try {
        const url = `${baseUrl}/units/${unitId}/features`;
        const response = await get(
          buildUrl(url, {
            skip,
            limit,
            name: filters.name,
            artifact_key: filters.artifactKey,
          }),
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching information package unit features:', error);
        throw error;
      }
    },
    [get],
  );

  const getArtifactContentById = useCallback(
    async (artifactId: string): Promise<InformationPackageArtifactContentApi> => {
      try {
        const response = await get(`${baseUrl}/artifact/${artifactId}/content`);
        return response.data;
      } catch (error) {
        console.error('Error fetching artifact content by ID:', error);
        throw error;
      }
    },
    [get],
  );

  return (
    <InformationPackageApiContext.Provider
      value={{
        getInformationPackages,
        deleteInformationPackage,
        createInformationPackage,
        getInformationPackageById,
        reindexInformationPackage,
        getInformationPackageUnitById,
        getInformationPackageArtifactById,
        getInformationPackageUnitFeatures,
        getArtifactContentById,
      }}
    >
      {children}
    </InformationPackageApiContext.Provider>
  );
}

export function useInformationPackageApi(): InformationPackageApiContextType {
  const context = useContext(InformationPackageApiContext);
  if (!context) {
    throw new Error('useInformationPackageApi must be used within a InformationPackageApiProvider');
  }
  return context;
}
